import { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WarningCircle } from '@phosphor-icons/react';
import { useDispatch } from 'react-redux';

import { AddBeneficiariesTotalAllocationGridStyled } from '../../AddYourBeneficiariesStyled';
import {
  BeneficiariesTotalAmountAllocationValue,
  BeneficiariesTotalPercentageAllocationValue,
} from 'enrollment/app/constants';
import { useConfigurationQuery } from 'theSource/queries';
import { api } from 'theSource/utils';
import { updateBenefitBeneficiaryData } from '../../services';
import { CheckCircleStyled } from 'enrollment/components/Header/components/CartDrawer/CartDrawerStyled';
import { setBeneficiarySelections } from '../../slice';
import { EsignatureModal } from '../../../Plans/EsignatureModal';
import { AdditionalPlanElectionDataDto } from '../../../Plans/PlanCard/types';
import { openEsignatureModal } from '../../../Plans/EsignatureModal/tests/utils';
import { closeSideDrawer } from 'enrollment/utils/common/Utils';

export const BeneficiaryTotalAllocation = ({
  dropdownAllocationValue,
  orgBenefitId,
  beneficiaries,
  primaryTotalAllocation,
  secondaryTotalAllocation,
  totalAllocation,
  zeroAllocation,
  emptyAllocation,
  isPrimary,
  setDisabled,
  emptyPrimaryBeneficiaries,
  coverageFields,
  autoSave = true,
  numCoverageLines,
  allBeneficiariesPerBenefit,
}: any) => {
  const [loader, setLoader] = useState(false);
  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');
  const theme = useTheme();
  const desktopViewConditions = useMediaQuery(theme.breakpoints.up('sm'));
  const [errorMsg, setErrorMsg] = useState('');
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const dispatch = useDispatch();
  const [initialRender, setInitialRender] = useState(true);
  const [successAllocation, setSuccessAllocation] = useState(false);

  const secondaryBeneficiaries = beneficiaries?.filter((beneficiary: any) => {
    return beneficiary?.primary_beneficiary === false;
  });

  const showEsignatureModal = coverageFields?.esign_data?.esignature_required;
  let beneficiaryEsign: any = null;

  const beneficiariesType = beneficiaries?.filter((beneficiary: any) => {
    return beneficiary?.primary_beneficiary === isPrimary;
  });

  useEffect(() => {
    const invalidMsg =
      document.querySelectorAll('.allocation-is-invalid').length > 0 ||
      document.querySelectorAll('.allocation-is-loading').length > 0 ||
      emptyPrimaryBeneficiaries;
    if (invalidMsg) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  });

  const handleElection = (
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    beneficiaryEsign =
      additionalPlanData?.esign_checked || additionalPlanData?.esign_password;
    saveBeneficiaries();
  };

  useEffect(() => {
    setErrorMsg(() => {
      if (emptyAllocation) {
        return t('allocationMissingError');
      } else if (zeroAllocation || totalAllocation !== 100) {
        return t('totalAllocationErrorMsg');
      } else {
        return '';
      }
    });
    if (
      primaryTotalAllocation / numCoverageLines === 100 &&
      ((secondaryBeneficiaries?.length > 0 &&
        secondaryTotalAllocation / numCoverageLines === 100) ||
        secondaryBeneficiaries?.length === 0) &&
      !zeroAllocation &&
      !emptyAllocation &&
      !initialRender
    ) {
      if (autoSave) {
        if (showEsignatureModal) {
          openEsignatureModal(orgBenefitId);
        } else {
          saveBeneficiaries();
        }
      } else {
        saveUnumBeneficiaries();
      }
    }
    setInitialRender(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAllocation]);

  const saveUnumBeneficiaries = () => {
    setLoader(true);
    const updatedBeneficiaries = allBeneficiariesPerBenefit;
    dispatch(
      updateBenefitBeneficiaryData({
        orgBenefitId,
        updatedBeneficiaries,
        updatedEsign: [],
      })
    );
    setSuccessAllocation(true);
    setLoader(false);
  };

  const saveBeneficiaries = () => {
    setLoader(true);
    api
      .put(
        `${lumosApiHost}/v2/self_service/beneficiaries/coverages/${orgBenefitId}/?cart_mode=true`,
        {
          beneficiaries: allBeneficiariesPerBenefit,
          beneficiary_esign: beneficiaryEsign,
          life_event_completed: false,
        }
      )
      .then((response: any) => {
        const updatedBeneficiaries = response?.data?.beneficiaries?.map(
          (beneficiary: any) => {
            if (beneficiary.id) {
              return { ...beneficiary, uniq_id: beneficiary.id };
            }
            return beneficiary;
          }
        );
        const updatedEsign = response?.data?.esignature_required;
        const updatedBeneficiarySelections =
          response?.data['beneficiary_selection_options'];
        dispatch(
          updateBenefitBeneficiaryData({
            orgBenefitId,
            updatedBeneficiaries,
            updatedEsign,
          })
        );
        dispatch(setBeneficiarySelections(updatedBeneficiarySelections));
        setSuccessAllocation(true);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        console.log(err?.response?.data?.details);
      });
  };

  const emptyFunction = () => {
    closeSideDrawer();
    return;
  };

  return (
    <>
      {beneficiariesType && (
        <AddBeneficiariesTotalAllocationGridStyled
          data-testid="total-allocation"
          id={`${
            isPrimary ? `primary` : `secondary`
          }-total-allocation-${orgBenefitId}`}
          beneficiaries={beneficiariesType}
          className={`${loader ? `allocation-is-loading` : ``}`}
        >
          {beneficiariesType?.length > 0 && (
            <div
              className={`d-flex semiBold ${desktopViewConditions ? 'body2' : 'caption'}`}
            >
              {desktopViewConditions && (
                <span className="pr-1">{t('common:total')}: </span>
              )}
              <div className="flex-grow-1 flex-md-grow-0">
                <span
                  style={{
                    color:
                      errorMsg === '' && totalAllocation === 100
                        ? '#2A7527'
                        : '#AE0016',
                  }}
                >
                  {desktopViewConditions && (
                    <span>{dropdownAllocationValue === 'amount' && '$'}</span>
                  )}
                  {totalAllocation}
                  {desktopViewConditions && (
                    <span>
                      {dropdownAllocationValue === 'percentage' && '%'}
                    </span>
                  )}
                </span>
              </div>
              {!desktopViewConditions && (
                <span>
                  {` / ${
                    dropdownAllocationValue === 'amount'
                      ? BeneficiariesTotalAmountAllocationValue
                      : dropdownAllocationValue === 'percentage'
                        ? BeneficiariesTotalPercentageAllocationValue
                        : null
                  }`}
                </span>
              )}
            </div>
          )}
          {beneficiariesType?.length > 0 ? (
            errorMsg !== '' ? (
              <div className="d-none d-lg-flex flex-row-reverse align-items-center allocation-is-invalid caption red">
                <div className="d-flex align-items-center">
                  <WarningCircle size={24} />
                  <div className="pl-1">{errorMsg}</div>
                </div>
              </div>
            ) : successAllocation || totalAllocation === 100 ? (
              autoSave && (
                <div className="d-none d-lg-flex flex-row-reverse align-items-center caption true">
                  <div className="d-flex align-items-center">
                    <CheckCircleStyled size={24} />
                    <div className="pl-1">{t('totalAllocationSuccessMsg')}</div>
                  </div>
                </div>
              )
            ) : null
          ) : null}
          {showEsignatureModal && (
            <EsignatureModal
              setEsignElectionData={handleElection}
              carrierLogoUrl={coverageFields?.carrier_logo_url}
              carrierName={coverageFields?.carrier_name}
              data={coverageFields?.esign_data}
              planId={orgBenefitId}
              hideChoosePlanButton={true}
              setModalRequired={emptyFunction}
            />
          )}
        </AddBeneficiariesTotalAllocationGridStyled>
      )}
    </>
  );
};
